import React, { useState } from "react";
import { CalendarX2, FilePlus2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { ContractMissingPayment } from "../../features/billing/models/ContractMissingPayment";
import { createMissingDraft } from "../../features/billing/slice-extensions/billing-slice-extension";
import { formatMoney } from "../../features/billing/utils/money";
import { ROUTES } from "../../routes";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

interface ContractsMissingPaymentListProps {
  contracts: ContractMissingPayment[];
  memberName: (memberId: string) => string;
  onChanged: () => void;
}

/**
 * Subscription contracts active last month with no payment covering it — the gaps
 * the monthly draft job should have filled, each with a one-click draft creation.
 */
const ContractsMissingPaymentList: React.FC<ContractsMissingPaymentListProps> = ({
  contracts,
  memberName,
  onChanged,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [busyId, setBusyId] = useState<string | null>(null);

  if (contracts.length === 0) return null;

  const handleCreateDraft = async (contractId: string) => {
    setBusyId(contractId);
    try {
      await dispatch(createMissingDraft(contractId)).unwrap();
      toast.success(t("billing.missingPayment.draftCreated"));
      onChanged();
    } catch (e) {
      toast.error((e as { message?: string })?.message ?? t("billing.missingPayment.draftFailed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-6">
      <h3 className="flex items-center gap-2 text-grey-10 dark:text-grey-90">
        <CalendarX2 size={20} className="text-grey-30 dark:text-grey-70" />
        {t("billing.missingPayment.title")}
        <span className="ml-2 text-sm font-normal text-grey-30 dark:text-grey-70">
          {contracts[0].periodStart.slice(0, 7)}
        </span>
      </h3>
      <div className="mt-3 space-y-2">
        {contracts.map((c) => (
          <div
            key={c.contractId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-grey-90 px-3 py-2 dark:border-primary-20"
          >
            <div className="text-sm">
              <Link
                to={ROUTES.MEMBER_DETAILS.replace(":id", c.memberId)}
                className="font-medium text-primary-40 hover:underline dark:text-primary-90"
              >
                {memberName(c.memberId)}
              </Link>
              <span className="ml-2 text-grey-30 dark:text-grey-70">
                {c.monthlyFeeAmountMinorUnits != null && c.monthlyFeeCurrency
                  ? t("billing.common.perMonth", {
                      amount: formatMoney(c.monthlyFeeAmountMinorUnits, c.monthlyFeeCurrency, getDisplayLocale()),
                    })
                  : t("billing.common.subscription")}
                {" · "}{c.periodStart} → {c.periodEnd}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busyId === c.contractId}
              onClick={() => handleCreateDraft(c.contractId)}
            >
              <FilePlus2 size={16} />
              <span>{t("billing.actions.createDraft")}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractsMissingPaymentList;
